import { Button, success } from '@valid-eval/shared-react-components';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import GenericModal from 'components/GenericModal';
import { useAddDocument } from 'data/features/documentsHooks';
import { Event } from 'data/features/eventsTypes';
import useBooleanFlag from 'utils/hooks/useBooleanFlag';
import { useShowErrorIfRejected } from 'utils/hooks/useShowErrorIfRejected';
import DocumentForm, { DocumentFormValues } from './DocumentForm';

type AddDocumentProps = {
  event: Event;
};

export const AddDocument = ({ event }: AddDocumentProps) => {
  const { t } = useTranslation();
  const [show, open, close] = useBooleanFlag(false);
  const { addDocumentToEvent, saving } = useAddDocument();
  const showErrorIfRejected = useShowErrorIfRejected();

  const methods = useForm<DocumentFormValues>({
    mode: 'all',
    defaultValues: {},
  });

  const onSubmit = async (document: DocumentFormValues) => {
    const recipients = (document.recipient_values?.filter(Boolean) as ('teams' | 'judges')[]) || [];
    document.recipient = recipients.length === 1 ? recipients[0] : 'teams_and_judges';

    document.recipient_values = undefined;

    const [, hasError] = showErrorIfRejected(await addDocumentToEvent(event.id, document));
    if (!hasError) {
      success(t('event.form.add_document_success'));
      methods.reset();
      close();
    }
  };

  const handleCancel = () => {
    methods.reset();
    close();
  };

  return (
    <>
      <GenericModal
        name="add-document"
        show={show}
        onCancel={handleCancel}
        title={t('event.form.add_document_title')}
        body={<DocumentForm methods={methods} event={event} onSubmit={onSubmit} />}
        onConfirm={methods.handleSubmit(onSubmit)}
        confirmButton={t('event.form.add_document_confirm')}
        cancelButton={t('common.cancel')}
        isButtonDisabled={saving}
        isCancelButtonDisabled={saving}
      />
      <Button variant="success" className="py-2 px-4" onClick={open}>
        <i className="fa-solid fa-plus" />
        {t('event.form.add_new')}
      </Button>
    </>
  );
};
